import React, { useState, useRef } from "react";
import {
    Database,
    ExternalLink,
    Globe,
    FileText,
    Image,
    Check,
    X,
    Upload,
    ShieldAlert,
} from "lucide-react";
import CreateBlueprint from "../components/CreateBlueprint";
import BlueprintAuthorCard from "../components/BlueprintAuthorCard";
import BlueprintSubscriberCard from "../components/BlueprintSubscriberCard";
import { LaunchChecklist } from "../../components/LaunchChecklist";
import { useTranslation } from "react-i18next";
import { useUploader } from "@utils";
import { Alert, AlertDescription, AlertTitle } from "@shadcn-in-nx/ui/alert";

function OverviewPanel({
    databases,
    onOpenApp,
    workflowName = "",
    workflowDescription = "",
    workflowIcon = null,
    isUpdatingWorkflow = false,
    onUpdateWorkflow,
    isTokenPublic,
    onEnterIntegrations,
    onSwitchPanel,
    maintainableBlueprint = null,
    blueprintSubscription = null,
    onBlueprintCreated,
}) {
    const { t } = useTranslation();
    const [editingName, setEditingName] = useState(false);
    const [editingDescription, setEditingDescription] = useState(false);
    const [tempName, setTempName] = useState(workflowName);
    const [tempDescription, setTempDescription] = useState(workflowDescription);
    const fileInputRef = useRef(null);

    // Use uploader hook for icon upload
    const { uploadFiles, loading: isUploadingIcon } = useUploader({
        onFileUpload: async (fileData) => {
            // Update workflow with the permanent URL of the uploaded icon
            if (fileData.permanentUrl) {
                await onUpdateWorkflow({ iconUrl: fileData.permanentUrl });
            }
        },
        isPublic: true, // Make the icon publicly accessible
    });

    // Update temp values when props change
    React.useEffect(() => {
        setTempName(workflowName);
    }, [workflowName]);

    React.useEffect(() => {
        setTempDescription(workflowDescription);
    }, [workflowDescription]);

    const handleSaveName = () => {
        if (tempName !== workflowName && tempName.trim()) {
            onUpdateWorkflow({ name: tempName.trim() });
        }
        setEditingName(false);
    };

    const handleCancelName = () => {
        setTempName(workflowName);
        setEditingName(false);
    };

    const handleSaveDescription = () => {
        if (tempDescription !== workflowDescription) {
            onUpdateWorkflow({ description: tempDescription.trim() || null });
        }
        setEditingDescription(false);
    };

    const handleCancelDescription = () => {
        setTempDescription(workflowDescription);
        setEditingDescription(false);
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith("image/")) {
                alert(
                    t("appPreview.invalidFileType") ||
                        "Please select an image file"
                );
                return;
            }
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                alert(
                    t("appPreview.fileTooLarge") ||
                        "File size must be less than 5MB"
                );
                return;
            }
            // Upload the file using the uploader hook
            await uploadFiles([file]);
            // Reset the file input
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    return (
        <div className="w-full h-full overflow-auto bg-gray-50 p-4 md:p-6 pb-8">
            <div className="max-w-4xl mx-auto space-y-6">
                {/* App Information Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        {t("appPreview.appInformation") || "App Information"}
                    </h2>
                    <div className="space-y-4">
                        {/* App Icon */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t("appPreview.appIcon") || "App Icon"}
                            </label>
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                                    {workflowIcon ? (
                                        <img
                                            src={workflowIcon}
                                            alt="App icon"
                                            className="w-full h-full object-cover rounded-lg"
                                        />
                                    ) : (
                                        <Image className="w-6 h-6 text-gray-400" />
                                    )}
                                </div>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                                <button
                                    onClick={() =>
                                        fileInputRef.current?.click()
                                    }
                                    disabled={isUploadingIcon}
                                    className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Upload className="w-4 h-4" />
                                    {isUploadingIcon
                                        ? t("common.loading") || "Uploading..."
                                        : t("appPreview.uploadIcon") ||
                                          "Upload Icon"}
                                </button>
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                                {t("appPreview.iconRequirements") ||
                                    "Recommended: Square image, max 5MB"}
                            </p>
                        </div>

                        {/* App Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t("appPreview.appName") || "App Name"}
                            </label>
                            {editingName ? (
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={tempName}
                                        onChange={(e) =>
                                            setTempName(e.target.value)
                                        }
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder={
                                            t("appPreview.enterAppName") ||
                                            "Enter app name"
                                        }
                                        autoFocus
                                    />
                                    <button
                                        onClick={handleSaveName}
                                        disabled={
                                            isUpdatingWorkflow ||
                                            !tempName.trim()
                                        }
                                        className="p-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title={t("common.save") || "Save"}
                                    >
                                        <Check className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={handleCancelName}
                                        disabled={isUpdatingWorkflow}
                                        className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title={t("common.cancel") || "Cancel"}
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <div
                                    onClick={() => setEditingName(true)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                                >
                                    {workflowName || (
                                        <span className="text-gray-400">
                                            {t("appPreview.clickToAddName") ||
                                                "Click to add app name"}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* App Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t("appPreview.appDescription") ||
                                    "App Description"}
                            </label>
                            {editingDescription ? (
                                <div className="space-y-2">
                                    <textarea
                                        value={tempDescription}
                                        onChange={(e) =>
                                            setTempDescription(e.target.value)
                                        }
                                        rows={4}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                        placeholder={
                                            t(
                                                "appPreview.enterAppDescription"
                                            ) || "Enter app description"
                                        }
                                        autoFocus
                                    />
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={handleSaveDescription}
                                            disabled={isUpdatingWorkflow}
                                            className="px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Check className="w-4 h-4" />
                                            {t("common.save") || "Save"}
                                        </button>
                                        <button
                                            onClick={handleCancelDescription}
                                            disabled={isUpdatingWorkflow}
                                            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <X className="w-4 h-4" />
                                            {t("common.cancel") || "Cancel"}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    onClick={() => setEditingDescription(true)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors min-h-[80px]"
                                >
                                    {workflowDescription ? (
                                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                            {workflowDescription}
                                        </p>
                                    ) : (
                                        <span className="text-gray-400 text-sm">
                                            {t(
                                                "appPreview.clickToAddDescription"
                                            ) || "Click to add app description"}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Create Blueprint */}
                        <CreateBlueprint onCreated={onBlueprintCreated} hasMaintainableBlueprint={!!maintainableBlueprint} />

                        {/* Blueprint Author Card */}
                        {maintainableBlueprint && (
                            <BlueprintAuthorCard blueprint={maintainableBlueprint} />
                        )}

                        {/* Blueprint Subscriber Card */}
                        {blueprintSubscription && (
                            <BlueprintSubscriberCard subscription={blueprintSubscription} />
                        )}
                    </div>
                </div>

                {/* App Link Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Globe className="w-5 h-5 text-blue-600" />
                        {t("appPreview.appAccess") || "App Access"}
                    </h2>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={onOpenApp}
                            className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
                        >
                            <ExternalLink className="w-5 h-5" />
                            {t("appPreview.openApp") || "Open App"}
                        </button>
                    </div>
                    <p className="mt-3 text-sm text-gray-500">
                        {t("appPreview.openAppDescription") ||
                            "Open your app in a new tab"}
                    </p>
                </div>

                {/* Launch Checklist */}
                <LaunchChecklist onSwitchPanel={onSwitchPanel} onOpenApp={onOpenApp} />

                {/* Security Note */}
                {isTokenPublic && (
                    <Alert
                        className="bg-yellow-50 border-yellow-200 cursor-pointer hover:bg-yellow-100 transition-colors"
                        onClick={onEnterIntegrations}
                    >
                        <ShieldAlert className="h-4 w-4 text-yellow-600" />
                        <AlertTitle className="text-yellow-800">
                            {t("appPreview.securityNote.title") ||
                                "Security Note"}
                        </AlertTitle>
                        <AlertDescription className="text-yellow-700">
                            {t("appPreview.securityNote.description") ||
                                "Your current security API key is enabled to allow recording of all integrations. Once you're ready and have tested everything, make sure to turn this off."}
                        </AlertDescription>
                    </Alert>
                )}

                {/* Database Tables Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Database className="w-5 h-5 text-purple-600" />
                        {t("appPreview.databaseTables") || "Database Tables"}
                    </h2>
                    {databases && databases.length > 0 ? (
                        <div className="space-y-3">
                            {databases.map((db, dbIndex) => (
                                <div
                                    key={db.id || dbIndex}
                                    className="space-y-3"
                                >
                                    {/* Tables List */}
                                    {db.tables && db.tables.length > 0 ? (
                                        <div className="space-y-2">
                                            {db.tables.map(
                                                (table, tableIndex) => (
                                                    <div
                                                        key={
                                                            table.id ||
                                                            tableIndex
                                                        }
                                                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                                                            <div>
                                                                <h4 className="text-sm font-medium text-gray-900">
                                                                    {table.name ||
                                                                        `Table ${
                                                                            tableIndex +
                                                                            1
                                                                        }`}
                                                                </h4>
                                                                {table.description && (
                                                                    <p className="text-xs text-gray-500 mt-0.5">
                                                                        {
                                                                            table.description
                                                                        }
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => {
                                                                // Navigate to specific table
                                                                const tableUrl = `/databases/${db.id}/tables/${table.id}`;
                                                                window.open(
                                                                    tableUrl,
                                                                    "_blank"
                                                                );
                                                            }}
                                                            className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors flex items-center gap-1.5"
                                                        >
                                                            {t(
                                                                "appPreview.viewTable"
                                                            ) || "View Table"}
                                                            <ExternalLink className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    ) : (
                                        <div className="text-center py-4">
                                            <p className="text-xs text-gray-500">
                                                {t("appPreview.noTables") ||
                                                    "No tables in this database"}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <Database className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-sm text-gray-500">
                                {t("appPreview.noDatabase") ||
                                    "No database configured for this workflow"}
                            </p>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}

export default OverviewPanel;
